/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { History, RefreshCw, ShieldCheck, FileText, Send, Eye, Download } from 'lucide-react';
import { getFseAudit } from '@/lib/fseLombardia';

const formatDT = (iso) => { try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso || ''; } };

const ACTION_META = {
  consent_granted: { label: 'Consenso concesso', Icon: ShieldCheck, tone: 'text-emerald-600' },
  consent_revoked: { label: 'Consenso revocato', Icon: ShieldCheck, tone: 'text-amber-600' },
  document_created: { label: 'Documento creato', Icon: FileText, tone: 'text-primary' },
  document_viewed: { label: 'Documento visto', Icon: Eye, tone: 'text-muted-foreground' },
  document_downloaded: { label: 'Documento scaricato', Icon: Download, tone: 'text-muted-foreground' },
  document_exported_mtls_sim: { label: 'Trasmesso a SISS (sim)', Icon: Send, tone: 'text-emerald-600' },
};

export const AuditLog = ({ refreshTick = 0 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await getFseAudit({ limit: 60 }); setItems(r.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshTick]);

  return (
    <div className="space-y-2" data-testid="fse-audit-log">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-primary" />
        <h2 className="font-display font-semibold">Audit log ({items.length})</h2>
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto" disabled={loading} data-testid="fse-audit-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {loading && [...Array(4)].map((_, i) => <Skeleton key={`audit-skel-${i}`} className="h-14 rounded-lg" />)}
      {!loading && items.length === 0 && (
        <Card><CardContent className="p-4 text-center text-sm text-muted-foreground">Nessun evento registrato.</CardContent></Card>
      )}

      <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
        {items.map((e) => {
          const meta = ACTION_META[e.action] || { label: e.action, Icon: History, tone: 'text-muted-foreground' };
          const Icon = meta.Icon;
          return (
            <li key={e.id} className="p-3 flex items-start gap-3 text-sm" data-testid={`audit-${e.id}`}>
              <Icon className={`h-4 w-4 mt-0.5 ${meta.tone}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{meta.label}</div>
                <div className="text-[11px] text-muted-foreground">
                  {formatDT(e.at)} · {e.actor_role} · target: <span className="font-mono">{e.target_id?.slice(0, 8)}…</span>
                  {e.reason && <> · <em>{e.reason}</em></>}
                </div>
              </div>
              {e.mtls_simulated && <Badge variant="outline" className="text-[10px]">mTLS sim</Badge>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AuditLog;
